import type { JobProgress } from '@/shared/types';
import { formatEta, stageDone } from '@/shared/utils/jobProgress';
import { useEtaCountdown } from '@/shared/hooks/useEtaCountdown';

export function JobProgressChecklist({ progress }: { progress: JobProgress }) {
  const { characters, keyframes, narrationReady, scenes, etaSeconds } = progress;
  const scenesDone = stageDone(scenes.completed, scenes.total);
  const scenesPct = scenes.total > 0 ? Math.round((scenes.completed / scenes.total) * 100) : 0;
  const remainingSeconds = useEtaCountdown(etaSeconds);

  return (
    <div className="job-progress">
      {remainingSeconds !== undefined && (
        <div className="job-progress__eta">{formatEta(remainingSeconds)}</div>
      )}
      <ProgressRow
        label="Characters"
        done={stageDone(characters.ready, characters.total)}
        count={`${characters.ready}/${characters.total}`}
      />
      <ProgressRow
        label="Keyframes"
        done={stageDone(keyframes.ready, keyframes.total)}
        count={`${keyframes.ready}/${keyframes.total}`}
      />
      <ProgressRow
        label="Narration & music"
        done={narrationReady}
        count={narrationReady ? 'ready' : undefined}
      />
      <ProgressRow
        label="Scenes rendering"
        done={scenesDone}
        count={`${scenes.completed}/${scenes.total}`}
      />
      <div className="job-progress__bar-track">
        <span className="job-progress__bar-fill" style={{ width: `${scenesPct}%` }} />
      </div>
      {scenes.failed > 0 && (
        <div className="job-progress__failed">{scenes.failed} scene(s) failed — retrying</div>
      )}
    </div>
  );
}

function ProgressRow({
  label,
  done,
  count,
}: {
  label: string;
  done: boolean;
  count?: string;
}) {
  return (
    <div className="job-progress__row">
      <span className={`job-progress__icon${done ? ' job-progress__icon--done' : ''}`}>
        {done ? '✓' : ''}
      </span>
      <span className="job-progress__label">{label}</span>
      {count && <span className="job-progress__count">{count}</span>}
    </div>
  );
}
